import { DEFAULT_PLACEHOLDER } from '@constants/generic';
import { MemberTransactionType } from '@types';
import { formatISODate } from '@utils/date';

import { getPaymentStatusDisplayText } from './paymentChannel';
import { getTransactionStatus } from './transactionStatus';

export const getPaymentDetails = (memberDetails: MemberTransactionType) => [
  ...(!memberDetails.fee_amount_configuration
    ? [
        {
          title: 'Total Amount Requested',
          value: parseFloat(
            (
              Number(memberDetails.total_amount) +
              Number(memberDetails.closed_amount) +
              Number(memberDetails.total_discount_amount ?? 0)
            ).toString()
          ).toFixed(2),
          subValue: parseFloat(memberDetails.total_amount).toFixed(2)
        }
      ]
    : []),
  ...(Number(memberDetails.total_paid_amount) !== 0
    ? [
        {
          title: 'Total Amount Paid',
          value: parseFloat(memberDetails.total_paid_amount).toFixed(2),
          status: 'Paid',
          ...(Number(memberDetails.total_paid_amount) >
            Number(memberDetails.total_settled_amount) && {
            subText: 'Total amount paid includes settled and processing'
          })
        }
      ]
    : []),
  ...(memberDetails.total_discount_amount &&
  parseFloat(memberDetails.total_discount_amount) > 0
    ? [
        {
          title: 'Total Amount Discounted',
          value: parseFloat(memberDetails.total_discount_amount).toFixed(2),
          status: 'Discount'
        }
      ]
    : []),
  ...(Number(memberDetails.total_amount) !==
  Number(memberDetails.total_paid_amount)
    ? [
        {
          title: 'Remaining Balance',
          value: parseFloat(
            (
              Number(memberDetails.total_amount) -
              Number(memberDetails.total_paid_amount)
            ).toString()
          ).toFixed(2),
          status: 'Pending',
          dueDate: memberDetails.due_at
            ? `Requested on ${formatISODate({
                date: memberDetails.due_at,
                format: 'DD MMM YYYY'
              })}`
            : DEFAULT_PLACEHOLDER
        }
      ]
    : []),
  ...(Number(memberDetails.closed_amount) !== 0
    ? [
        {
          title: 'Remaining Amount',
          value:
            memberDetails.closed_amount &&
            parseFloat(memberDetails.closed_amount).toFixed(2),
          status: 'Closed',
          dueDate: memberDetails.closed_at
            ? `${formatISODate({
                date: memberDetails.closed_at,
                format: 'MMM DD YYYY'
              })}`
            : DEFAULT_PLACEHOLDER,
          subText: memberDetails.close_payment_note
            ? memberDetails.close_payment_note
            : DEFAULT_PLACEHOLDER
        }
      ]
    : [])
];

export const getDownloadReceiptDetails = (
  memberDetails: Pick<
    MemberTransactionType,
    'id' | 'is_split_payment' | 'split_payment_order_id' | 'total_paid_amount'
  >,
  fallbackSplitPaymentOrderId?: string
) => {
  const isSplitPayment =
    memberDetails.is_split_payment && memberDetails.total_paid_amount !== '0';
  const splitPaymentOrderId =
    memberDetails.split_payment_order_id || fallbackSplitPaymentOrderId;

  return {
    downloadReciptId:
      isSplitPayment && splitPaymentOrderId
        ? splitPaymentOrderId
        : memberDetails.id,
    isSplitPayment
  };
};

export const getQuickCollectInfo = (memberDetails: MemberTransactionType) =>
  [
    {
      title: 'Status',
      value: getPaymentStatusDisplayText(
        getTransactionStatus(
          memberDetails.status,
          memberDetails.settlement_status
        ),
        memberDetails.payment_mode,
        memberDetails.payment_method
      )
    },
    ...(memberDetails.branch_name
      ? [
          {
            title: 'Branch',
            value: memberDetails.branch_name
          }
        ]
      : []),
    {
      title: 'Requested on',
      value: formatISODate({
        date: memberDetails.requested_on,
        format: 'DD MMM YYYY',
        timeZone: 'Asia/Kolkata'
      })
    },
    { title: 'Payment To', value: memberDetails.payment_to },
    { title: 'Transaction ID', value: memberDetails.id },
    { title: 'Notes', value: memberDetails.payment_description },
    {
      title: 'Settled On',
      value: formatISODate({
        date: memberDetails.settled_at,
        format: 'DD MMM YYYY at hh:mm am/pm'
      })
    },
    ...(memberDetails.utr && memberDetails.payment_mode !== 'manual'
      ? [{ title: 'UTR Number', value: memberDetails.utr }]
      : []),
    ...(memberDetails.paid_at
      ? [
          {
            title: 'Paid On',
            value: formatISODate({
              date: memberDetails.paid_at,
              format:
                memberDetails.payment_mode === 'automatic'
                  ? 'DD MMM YYYY at hh:mm am/pm'
                  : 'DD MMM YYYY'
            })
          }
        ]
      : []),
    ...(memberDetails.marked_paid_at
      ? [
          {
            title: 'Marked On',
            value: formatISODate({
              date: memberDetails.marked_paid_at,
              format: 'DD MMM YYYY at hh:mm am/pm'
            })
          }
        ]
      : []),
    ...(memberDetails.member_id
      ? [
          {
            title: 'Member ID',
            value: memberDetails.member_id
          }
        ]
      : []),
    ...(memberDetails.merchant_order_id
      ? [
          {
            title: 'Merchant Order ID',
            value: memberDetails.merchant_order_id
          }
        ]
      : [])
  ].filter((info) => info.value);
